import { getReferralUrlWithPromo } from '@frontend/api-client';
import { MEMBER_REFERRAL_COPY_DEFAULT_VALUE, useCallback, useFeatureValue, useMemo } from '@frontend/react';
import { ActionIcon, Icon, Text, XStack, YStack, showToast } from '@frontend/web-react';
import { useAnalytics, useRetrieveClientQuery } from '@frontend/web/hooks';

export const InviteFriends = () => {
  const { data: client } = useRetrieveClientQuery();
  const { track } = useAnalytics();
  const { value: memberReferralCopy } = useFeatureValue('member_referral_copy', MEMBER_REFERRAL_COPY_DEFAULT_VALUE);

  const referralUrlWithPromo = useMemo(() => {
    return getReferralUrlWithPromo(client?.referralUrl);
  }, [client?.referralUrl]);

  const copyReferralUrlToClipboard = useCallback(() => {
    if (!referralUrlWithPromo) return;
    track({ event: 'referralLinkCopied', topic: 'invite' });
    navigator.clipboard.writeText(referralUrlWithPromo);
    showToast({
      title: 'Referral link copied!',
      variant: 'success',
    });
  }, [referralUrlWithPromo, track]);

  const handleShare = useCallback(async () => {
    if (!referralUrlWithPromo) return;

    if (navigator.share && memberReferralCopy) {
      try {
        await navigator.share({
          text: memberReferralCopy.replace(/\*\*/g, ''),
          title: 'Join Duckbill',
          url: referralUrlWithPromo,
        });
        track({ event: 'referralLinkCopied', topic: 'invite' });
      } catch (error) {
        if ((error as Error).name !== 'AbortError') {
          copyReferralUrlToClipboard();
        }
      }
    } else {
      copyReferralUrlToClipboard();
    }
  }, [referralUrlWithPromo, memberReferralCopy, copyReferralUrlToClipboard, track]);

  if (!referralUrlWithPromo) {
    return null;
  }

  return (
    <YStack className="bg-cool-100 p-lg rounded-lg">
      <YStack className="flex-col md:flex-row" gap="lg">
        <YStack gap="sm">
          <Text className="font-medium text-textPrimary" type="p-md">
            Invite friends
          </Text>
          <Text className="text-textSecondary" type="p-sm">
            {memberReferralCopy}
          </Text>
        </YStack>
        <XStack alignItems="center" className="w-full md:w-auto" gap="sm">
          <XStack
            alignItems="center"
            // eslint-disable-next-line tailwindcss/no-arbitrary-value
            className="flex-1 md:min-w-[250px] rounded-lg border-none bg-white p-md gap-xs h-[56px]"
          >
            <Text className="grow-1 text-textPrimary text-p-md truncate" title={referralUrlWithPromo}>
              {referralUrlWithPromo.length > 17 ? `${referralUrlWithPromo.substring(0, 17)}...` : referralUrlWithPromo}
            </Text>
            <button
              aria-label="Copy referral link"
              // eslint-disable-next-line tailwindcss/no-arbitrary-value
              className="bg-neutral-900 text-white flex items-center justify-center w-[68px] px-[11px] py-[5px] rounded-lg text-p-sm font-medium hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer shrink-0"
              onClick={(e) => {
                e.stopPropagation();
                copyReferralUrlToClipboard();
              }}
              type="button"
            >
              Copy
            </button>
          </XStack>
          <ActionIcon
            aria-label="Share referral link"
            className="rounded-full! bg-neutral-900! text-white! hover:opacity-90! transition-opacity"
            onClick={handleShare}
            role="button"
            size="lg"
            variant="filled"
          >
            <Icon color="white" name="IconShare2" />
          </ActionIcon>
        </XStack>
      </YStack>
    </YStack>
  );
};
